import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Chip,
  OutlinedInput,
  Button,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  selectedReportFields,
  selectedReportFilters,
  allSitesFiberSitePlanning,
} from '../../common/atom/atoms';
import React, { useState, useEffect } from 'react';
import { getAllFiberSitePlanningSites } from './functions/get_all_fiber_sites.function';
import { getFieldDisplayName } from '../../common/functions/fieldNameMapping.function';
import { useFiberDashboard } from './context/fiberDashboardContext';

export default function ApplyFiltersGenerateReportFiber() {
  const selectedFields = useAtomValue(selectedReportFields);
  const setSelectedFilters = useSetAtom(selectedReportFilters);
  const { dashboardData } = useFiberDashboard();

  const [filters, setFilters] = useState({});
  const [selectedField, setSelectedField] = useState(null);
  const [fieldData, setFieldData] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayedValues, setDisplayedValues] = useState({});

  // Initialize filters when selected fields change
  useEffect(() => {
    const initialFilters = {};
    selectedFields.forEach((field) => {
      initialFilters[field] = [];
    });
    setFilters(initialFilters);
    setSelectedFilters(initialFilters);
    // Select the first field by default
    if (selectedFields.length > 0) {
      setSelectedField(selectedFields[0]);
    }
  }, [selectedFields, setSelectedFilters]);

  // Extract field data from dashboard data when selected fields change
  useEffect(() => {
    if (selectedFields.length > 0 && dashboardData.length > 0) {
      extractFieldData();
    }
  }, [selectedFields, dashboardData]);

  // Update global state when filters change
  useEffect(() => {
    setSelectedFilters(filters);
  }, [filters, setSelectedFilters]);

  const extractFieldData = () => {
    setLoading(true);
    try {
      console.log('Extracting field data from dashboard data for:', selectedFields);
      console.log('Dashboard data length:', dashboardData.length);

      if (Array.isArray(dashboardData) && dashboardData.length > 0) {
        const newFieldData = {};
        const newDisplayedValues = {};

        selectedFields.forEach((field) => {
          const uniqueValues = [
            ...new Set(dashboardData.map((site) => site[field]).filter(Boolean)),
          ];
          const sortedValues = uniqueValues.sort();
          newFieldData[field] = sortedValues;

          // For large datasets, only show first 100 values initially
          if (sortedValues.length > 100) {
            newDisplayedValues[field] = sortedValues.slice(0, 100);
            console.log(
              `Field ${field} has ${sortedValues.length} unique values, showing first 100`
            );
          } else {
            newDisplayedValues[field] = sortedValues;
            console.log(`Field ${field} has ${sortedValues.length} unique values`);
          }

          // Special logging for site_code
          if (field === 'site_code') {
            console.log('Site codes found:', sortedValues.length);
            console.log('First 10 site codes:', sortedValues.slice(0, 10));
          }
        });

        setFieldData(newFieldData);
        setDisplayedValues(newDisplayedValues);
        console.log('Field data extracted from dashboard:', newFieldData);
      } else {
        console.log('No dashboard data available for field extraction');
        setFieldData({});
        setDisplayedValues({});
      }
    } catch (error) {
      console.error('Error extracting field data:', error);
      setFieldData({});
      setDisplayedValues({});
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFieldClick = (field) => {
    setSelectedField(field);
    setSearchTerm(''); // Reset search when switching fields
  };

  // Handle search in filter values
  const handleSearchChange = (event) => {
    const searchValue = event.target.value;
    setSearchTerm(searchValue);

    if (selectedField && fieldData[selectedField]) {
      const allValues = fieldData[selectedField];
      if (searchValue.trim() === '') {
        // Show first 100 values when search is cleared
        setDisplayedValues((prev) => ({
          ...prev,
          [selectedField]: allValues.slice(0, 100),
        }));
      } else {
        // Filter values based on search term
        const filteredValues = allValues.filter((value) =>
          value.toString().toLowerCase().includes(searchValue.toLowerCase())
        );
        setDisplayedValues((prev) => ({
          ...prev,
          [selectedField]: filteredValues.slice(0, 100), // Limit to 100 for performance
        }));
      }
    }
  };

  // Load more values for large datasets
  const loadMoreValues = () => {
    if (selectedField && fieldData[selectedField]) {
      const allValues = fieldData[selectedField];
      const currentDisplayed = displayedValues[selectedField] || [];
      const currentCount = currentDisplayed.length;
      const nextBatch = allValues.slice(currentCount, currentCount + 100);

      setDisplayedValues((prev) => ({
        ...prev,
        [selectedField]: [...currentDisplayed, ...nextBatch],
      }));
    }
  };

  const handleMultipleSelectChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleTextFilterChange = (field, value) => {
    // For text fields, store as array with single value for consistency
    setFilters((prev) => ({
      ...prev,
      [field]: value ? [value] : [],
    }));
  };

  const handleSelectAllForField = (field) => {
    const allValues = fieldData[field] || [];
    setFilters((prev) => ({
      ...prev,
      [field]: allValues,
    }));
  };

  const handleSelectAllDisplayed = (field) => {
    const displayedValuesForField = displayedValues[field] || [];
    setFilters((prev) => ({
      ...prev,
      [field]: [
        ...(prev[field] || []),
        ...displayedValuesForField.filter((val) => !(prev[field] || []).includes(val)),
      ],
    }));
  };

  const handleClearField = (field) => {
    setFilters((prev) => ({
      ...prev,
      [field]: [],
    }));
  };

  // Check if field should be a dropdown (has limited unique values)
  const shouldBeDropdown = (field) => {
    const values = fieldData[field] || [];
    // Always show as dropdown if we have values (we'll handle large datasets with pagination)
    return values.length > 0;
  };

  // Check if field has many values (needs special handling)
  const hasManyValues = (field) => {
    const values = fieldData[field] || [];
    return values.length > 100;
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        p: 2,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 2,
        position: 'relative',
      }}
    >
      {/* Left Panel - Selected Fields */}
      <Box
        sx={{
          width: 302,
          background: '#F8F8F8',
          borderRadius: 2,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: '#1D242E',
            mb: 1,
          }}
        >
          Selected Fields
        </Typography>
        {selectedFields.map((field, index) => (
          <Box
            key={field}
            sx={{
              background: field === selectedField ? '#F8E8E9' : 'transparent',
              p: 1,
              borderRadius: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': {
                backgroundColor: field === selectedField ? '#F8E8E9' : '#F0F0F0',
              },
            }}
            onClick={() => handleFieldClick(field)}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 400,
                color: '#1D242E',
              }}
            >
              {getFieldDisplayName(field)}
            </Typography>
            {filters[field] && filters[field].length > 0 && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#BD1D23',
                }}
              />
            )}
          </Box>
        ))}
      </Box>

      {/* Right Panel - Filter Configuration */}
      <Box
        sx={{
          flex: 1,
          background: 'white',
          borderRadius: 2,
          p: 3,
          border: '1px solid #E0E0E0',
        }}
      >
        {selectedField && (
          <Box>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                color: '#1D242E',
                mb: 2,
              }}
            >
              Filter: {getFieldDisplayName(selectedField)}
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography sx={{ color: '#666' }}>Loading data...</Typography>
              </Box>
            ) : (
              <>
                {/* Field info */}
                <Typography sx={{ fontSize: 12, color: '#999', mb: 1 }}>
                  Total values: {fieldData[selectedField]?.length || 0}
                  {hasManyValues(selectedField) && (
                    <span> (Showing first 100, use search to find specific values)</span>
                  )}
                </Typography>

                {shouldBeDropdown(selectedField) ? (
                  <Box>
                    {/* Search field for large datasets */}
                    {hasManyValues(selectedField) && (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={`Search ${getFieldDisplayName(selectedField)} values...`}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1, color: '#666' }}>🔍</Typography>,
                        }}
                      />
                    )}

                    {/* Optimized Autocomplete for large datasets */}
                    {hasManyValues(selectedField) ? (
                      <Autocomplete
                        multiple
                        options={displayedValues[selectedField] || []}
                        value={filters[selectedField] || []}
                        onChange={(event, newValue) => {
                          setFilters((prev) => ({
                            ...prev,
                            [selectedField]: newValue,
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder={`Select ${getFieldDisplayName(selectedField)} values`}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option}
                              size="small"
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Checkbox
                              checked={(filters[selectedField] || []).indexOf(option) > -1}
                              size="small"
                            />
                            {option}
                          </Box>
                        )}
                        filterOptions={(options) => options} // Disable built-in filtering since we handle it
                        noOptionsText={
                          displayedValues[selectedField]?.length === 0
                            ? searchTerm
                              ? 'No matching values found'
                              : 'No values available'
                            : 'Type to search...'
                        }
                        ListboxProps={{
                          style: {
                            maxHeight: '200px', // Limit height for performance
                          },
                        }}
                      />
                    ) : (
                      /* Regular Select for small datasets */
                      <FormControl fullWidth size="small">
                        <InputLabel>Select {getFieldDisplayName(selectedField)} values</InputLabel>
                        <Select
                          multiple
                          value={filters[selectedField] || []}
                          onChange={(e) =>
                            handleMultipleSelectChange(selectedField, e.target.value)
                          }
                          input={
                            <OutlinedInput
                              label={`Select ${getFieldDisplayName(selectedField)} values`}
                            />
                          }
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" />
                              ))}
                            </Box>
                          )}
                        >
                          {fieldData[selectedField]?.map((value) => (
                            <MenuItem key={value} value={value}>
                              <Checkbox
                                checked={(filters[selectedField] || []).indexOf(value) > -1}
                              />
                              {value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {/* Action buttons */}
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {hasManyValues(selectedField) ? (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSelectAllDisplayed(selectedField)}
                            sx={{ fontSize: 12 }}
                          >
                            Select All Displayed
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={loadMoreValues}
                            sx={{ fontSize: 12 }}
                            disabled={
                              (displayedValues[selectedField]?.length || 0) >=
                              (fieldData[selectedField]?.length || 0)
                            }
                          >
                            Load More ({displayedValues[selectedField]?.length || 0}/
                            {fieldData[selectedField]?.length || 0})
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleSelectAllForField(selectedField)}
                          sx={{ fontSize: 12 }}
                        >
                          Select All
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleClearField(selectedField)}
                        sx={{ fontSize: 12 }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Enter ${getFieldDisplayName(selectedField)} filter (e.g., partial match)`}
                    value={
                      Array.isArray(filters[selectedField])
                        ? filters[selectedField][0] || ''
                        : filters[selectedField] || ''
                    }
                    onChange={(e) => handleTextFilterChange(selectedField, e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#FAFAFA',
                      },
                    }}
                  />
                )}

                {/* Show selected values count */}
                {filters[selectedField] && filters[selectedField].length > 0 && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: '#666',
                      mt: 1,
                    }}
                  >
                    Selected {filters[selectedField].length} value(s)
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}

        {!selectedField && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#666',
            }}
          >
            <Typography>Select a field from the left panel to configure filters</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
